/**
 * The Point of Interest search implementation. Creates a new instance of service call. Parameters can be passed on
 * creation or provided via convenient functions that can be chained. The method go() performs the actual call. The
 * call is asynchronous therefore user have two options to handle response, either by passing callback function or by
 * using [promise](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise).
 *
 * @example
 *  var myFunction = function(result) { console.log(result); };
 *  tomtom.searchKey(<Your API key>); // this is optional if you passed global key earlier
 *  var poiSearch = tomtom.poiSearch().query('pizza').callback(myFunction).go();
 *
 * The SDK provides polyfill for browsers (IE<9) without native Ecma Script 6 promise support.
 *
 * @class poiSearch
 */
define('services/search/poiSearch',["./search"], function (search) {
    "use strict";

    var searchPrototype = search.prototype.constructor;

    function PoiSearch(options) {
        searchPrototype.call(this, options);

        this.fields.type = {
            defaultValue: this.TYPE.poi,
            visible: false
        };
    }

    /**
     * A setter and getter for _'protocol'_ attribute. Represents type of protocol used to perform service call.
     *
     * @method protocol
     * @chainable
     * @param {String} [protocol] Protocol type, one of _http_ or _https_.
     * @throws {TypeError} If the given argument cannot be converted to a valid value.
     * @return {search|String} Method returns the service instance allowing chaining or _protocol_
     * value if argument is omitted.
     */
    /**
     * A setter and getter for _'best result'_ option. It makes the service return only one, best match result.
     * This option overwrites both limit and offset parameters. It changes the response from array to single
     * result. Default value is false.
     *
     * @method bestResult
     * @chainable
     * @param {Boolean} [bestResult] Defines whether geocode service should return the best result.
     * @throws {TypeError} If the given argument cannot be converted to a valid value.
     * @return {search|Boolean} Method returns the service instance allowing chaining or _bestResult_ value
     * if argument is omitted.
     */
    /**
     * A setter and getter for _'center'_ attribute. This attribute allows to bias the search results.
     *
     * Supplying only that attribute - without _radius_ parameter - will bias search results only to
     * that area.
     *
     * The service is able to convert a number of popular formats into the point.
     * Supported formats are listed below:
     * * _[0, 0]_  //  array of latitude and longitude
     * * _"0,1"_  //  string of latitude and longitude, divided by comma
     * * _L.latLngBounds(...)_  //  leaflet bounds object
     * * _{lat: function() {...}, lng: function() {...}}_  //  object with two functions returning latitude and
     * longitude
     * * _{lat: 0, lon: 1}_    //  object with two properties: latitude and longitude
     * * _{x: 0, y: 1}_   //   object with two properties: x as latitude and y as longitude
     * * _{latitude: 0, longitude: 1}_   //   object with two properties: latitude and longitude
     *
     * @method center
     * @chainable
     * @param {Point} [center] Point with latitude and longitude data.
     * @throws {TypeError} If the given argument cannot be converted to a valid value.
     * @return {search|Point} Method returns the service instance allowing chaining or _center_ value
     * if argument is omitted.
     */
    /**
     * A setter and getter for _'boundingBox'_ attribute. The bounding box of the area which user
     * wants to search on. It can be omitted, then whole world is taken into consideration.
     *
     * Bounding box can exceed world boundaries (-180, -90, 180, 90) it is not an issue. The service will
     * internally normalize it by overwriting any exceeding value with maximal or minimal allowed.
     * Longitude will be recalculated to fit the allowed range, if bounding box includes edge of the world
     * it will be split into two bounding boxes and the response will contain merged results for both areas.
     *
     * The service is able to convert a number of popular formats into the bounding box.
     * Supported formats are listed below:
     * * _{minLon: 0, minLat: 0, maxLon: 1, maxLat: 1}_ // plain object with minLon, minLat, maxLon, maxLat
     * fields
     * * _L.latLngBounds(...)_ // leaflet bounds object
     * * _[0, 0, 1, 1]_ // array of numbers containing in order minLon, minLat, maxLon, maxLat
     * * _ol.Extent_ // open layers extend object
     * * _[ol.Coordinate, ol.Coordinate]_ // array of two open layers coordinate objects in order southWest,
     * northEast
     * * _[[0, 0], [1, 1]]_ // array of two arrays (southWest, northEast) with latitude and longitude values
     * * _"0,0,1,1"_ // comma separated string of number values minLon, minLat, maxLon, maxLat
     * * _[{lon: 0, lat: 0},{lon: 1, lat: 1}]_ // array of two plain objects (southWest, northEast)
     * with lat and lon fields
     * * _[{lng: 0, lat: 0},{lng: 1, lat: 1}]_ // array of two plain objects (southWest, northEast)
     * with lat and lng fields
     * * _[L.latLng(...), L.latLng(...)]_ // array of southWest and northEast leaflet latLng objects
     * * _google.maps.LatLngBounds_ // Google Maps bounds object
     * * _google.maps.LatLngBoundsLiteral_ // Google Maps bounds object
     * * _[LatLng, LatLng]_ // array of two google.maps.LatLng
     * * _[LatLngLiteral, LatLngLiteral]_ // array of two google.maps.LatLngLiteral
     *
     * @method boundingBox
     * @chainable
     * @param {Object|Array|String} [boundingBox] The bounding box of the area that search is supposed to
     * search on.
     * @throws {TypeError} If the given argument cannot be converted to a valid value.
     * @return {search|String} Method returns the service instance allowing chaining or converted bounding box
     * value if argument is omitted.
     */
    /**
     * A setter and getter for _'radius'_ attribute. Represents the area that results will be constrained to.
     * The passed value is meant to be specified in meters.
     *
     * This attribute is taken into the consideration only when _position_ attribute is set.
     *
     * @method radius
     * @chainable
     * @param {integer} [radius] Value of radius, specified in meters.
     * @throws {TypeError} If the given argument cannot be converted to a valid value.
     * @return {search|integer} Method returns the service instance allowing chaining or _radius_ value
     * if argument is omitted.
     */
    /**
     * A setter and getter for _'countrySet'_ attribute. Represents comma separated string of country codes
     * (e.g.: FR,ES). This will limit the search to the specified countries.
     *
     * @method countrySet
     * @chainable
     * @param {String} [countrySet] Comma separated list of country codes.
     * @throws {TypeError} If the given argument cannot be converted to a valid value.
     * @return {search|String} Method returns the service instance allowing chaining or _countrySet_ value
     * if argument is omitted.
     */
    /**
     * A setter and getter for _'offset'_ attribute. Represents starting offset of the returned results
     * within the full result set.
     *
     * An alias for this attribute is _'ofs'_. That means it is possible to set or get this attribute in
     * other way.
     *
     * Maximum value is 1900.
     *
     * @method offset
     * @chainable
     * @param {integer} [offset=0] Starting offset of the returned results within the full result set.
     * @throws {TypeError} If the given argument cannot be converted to a valid value.
     * @return {search|integer} Method returns the service instance allowing chaining or _offset_ value
     * if argument is omitted.
     */
    /**
     * A setter and getter for _'limit'_ attribute. Represents maximum number of responses that will be
     * returned.
     *
     * Maximum value is 100.
     *
     * @method limit
     * @chainable
     * @param {integer} [limit=10] Maximum number of responses that will be returned.
     * @throws {TypeError} If the given argument cannot be converted to a valid value
     * @return {search|integer} Method returns the service instance allowing chaining or _limit_ value
     * if argument is omitted.
     */
    /**
     * A setter and getter for _'typeahead'_ attribute. If the "typeahead" flag is set,
     * the query will be interpreted as a partial input and the search will enter predictive mode.
     *
     * @method typeahead
     * @chainable
     * @param {Boolean} [typeahead=false] Flag that determines state of typeahead.
     * @throws {TypeError} If the given argument cannot be converted to a valid value
     * @return {search|Boolean} Method returns the service instance allowing chaining or _typeahead_ value
     * if argument is omitted.
     */
    /**
     * A setter and getter for _'query'_ attribute. Value passed within this method represents text that will be
     * searched.
     *
     * This method is required to call, unless search value was passed by options.
     *
     * @method query
     * @chainable
     * @param {String} [query] Query string. Must be properly URL encoded.
     * @throws {TypeError} If the given argument cannot be converted to a valid value.
     * @return {search|String} Method returns the service instance allowing chaining or _query_ value
     * if argument is omitted.
     */
    /**
     * A setter and getter method for _'key'_ attribute for given search service instance.
     * Key can be set globally when initializing map with tomtom.key - for all services -
     * or tomtom.searchKey function - for all search service instances.
     *
     * Passing valid key in any way is required to enable search service.
     *
     * The API Key can be issued from http://developer.tomtom.com
     *
     * @method key
     * @chainable
     * @param {String} [key] key API Key valid for requested service.
     * @throws {TypeError} If the given argument cannot be converted to a valid value.
     * @return {search|String} Method returns the service instance allowing chaining or key value
     * if argument is omitted.
     */

    PoiSearch.prototype = new searchPrototype();
    PoiSearch.prototype.constructor = PoiSearch;

    /**
     * The function performing actual call to the web service. This method can be executed multiple times,
     * it is not changing parameters itself.
     *
     * Callback function takes a response parameter and fail callback takes error as only parameter.
     *
     * @method go
     * @param {Function} [callback] The callback function to be called on success.
     * @param {Function} [fail] The callback function to be called on failure. If success callback is not passed
     * fail callback is ignored. Returned promise can handle exceptional behaviour i.e. with then() method.
     * @return {Promise} If callback is omitted function returns promise
     */
    /**
     * A setter and getter for _callback_ function. This function is called after receiving successful response
     * from the service. It takes one argument which is the response - JSON described in go() function
     * documentation.
     *
     * @method callback
     * @chainable
     * @param {Function} [callback] The callback function to be called on success.
     * @throws {TypeError} if the given argument is not a function
     * @return {search} Method returns the service instance allowing chaining or function if argument is omitted.
     */
    /**
     * A setter and getter for the fail callback function. This function is called after an unsuccessful response
     * from the service or after a communication error. It takes one argument which is the error description.
     *
     * This parameter is ignored if callback function is not passed and therefore promise mechanism is used to
     * handle the response. When using promises, errors are handled with the second attribute of the then() method
     * or with the catch() method.
     *
     * By default the fail function is:
     * ```javascript
     *
     * function(error) {
         *   console.error(error);
         *   throw new Error(error);
         * }
     * ```
     * @method fail
     * @chainable
     * @param {Function} [fail] The callback function to be called on a failure. By default, an error is logged on
     *     the console and thrown.
     * @throws {TypeError} If the given argument is not a function.
     * @return {search|Function} Returns the service instance that allows chaining or a function, if the argument
     *     is omitted.
     */
    /**
     * Creates new instance of point of interest search service.
     *
     * @method poiSearch
     *
     * @param {Object}  [options] Options to be passed to the structured search call.
     * @param {String}  [options.key] The API Key issued from <a
     *     href="http://developeWr.tomtom.com">http://developer.tomtom.com</a>.
     * @param {String}  [options.query] Query string. Must be properly URL encoded.
     * @param {Boolean} [options.typeahead=false] If the "typeahead" flag is set,
     *                  the query will be interpreted as a partial input and the search will enter predictive mode.
     * @param {integer} [options.limit=10] Maximum number of responses that will be returned.
     * @param {integer} [options.offset=0] Starting offset of the returned results within the full result set.
     * @param {String}  [options.countrySet] Comma separated string of country codes (e.g.: FR,ES).
     *                  This will limit the search to the specified countries.
     * @param {integer} [options.radius] If radius and position are set, the results will be constrained
     *                  to the defined area. The radius parameter is specified in meters.
     * @param {Object|Array|String} [options.boundingBox] Area (two points with latitude and longitude) that search
     *                  will look for results onto.
     * @param {Point}   [options.center] Point of current area center.
     * @param {Boolean} [options.protocol] Protocol's type, one of "http" or "https".
     * @param {Function} [options.fail] The callback function to be called on failure.
     * @param {Function} [options.callback] Specifies the callback method. Only used where format is jsonp.
     *
     * @example
     * ```javascript
     *
     *  var myFunction = function(result) { console.log(result); };
     *  tomtom.searchKey(<Your API key>); // this is optional if you passed global key earlier
     *  var poiSearch = tomtom.poiSearch().query('pizza').callback(myFunction).go();
     *
     *  ```
     */
    return function (options) {
        return (new PoiSearch(options)).construct();
    };
});
